import { useMemo } from 'react';
import { useAdminGetStripePriceEntitlements } from '@/api/auth/admin/admin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatEntitlementRule } from '@/lib/entitlements';
import type { BillingScenario } from '@/lib/billing-scenarios';

interface ProductPriceListProps {
  productId: string;
  /**
   * The billing scenarios the viewing organization is eligible for. Only entitlements tagged with
   * one of these scenarios are shown, so the list reflects what the API would actually offer the
   * organization for this product. Entitlements with no scenario are not applicable to any tier and
   * are excluded, matching the server-side plan resolution.
   */
  eligibleScenarios: readonly BillingScenario[];
}

/**
 * Read-only view of the Stripe price list applicable to an organization for a product: the product's
 * configured Stripe price entitlements narrowed to the organization's eligible billing scenarios.
 * Shows scenario, granted rule, quantity, base/add-on, and tier-default. Monetary amounts are not
 * shown because the admin API does not currently expose unit amount / currency on these contracts.
 */
export function ProductPriceList({ productId, eligibleScenarios }: ProductPriceListProps) {
  const { data, isLoading, error } = useAdminGetStripePriceEntitlements(productId);
  const allPrices = data?.data?.stripePriceEntitlements ?? [];

  // Narrow the product's full price list to the entitlements the organization is actually eligible
  // for, so the admin view matches what the billing plans API resolves for this organization.
  const prices = useMemo(
    () =>
      allPrices.filter((price) => !!price.scenario && eligibleScenarios.includes(price.scenario)),
    [allPrices, eligibleScenarios]
  );

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }

  if (error) {
    return <p className="text-sm text-destructive">Failed to load price list.</p>;
  }

  if (prices.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No Stripe prices apply to this organization for this product.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Stripe Price</TableHead>
          <TableHead>Scenario</TableHead>
          <TableHead>Rule</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Quantity</TableHead>
          <TableHead>Tier default</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {prices.map((price) => (
          <TableRow key={`${price.stripePriceId}:${price.productId}`}>
            <TableCell className="font-mono text-xs">{price.stripePriceId}</TableCell>
            <TableCell>{price.scenario ?? '—'}</TableCell>
            <TableCell>{formatEntitlementRule(price.entitlementRule)}</TableCell>
            <TableCell>{price.basePriceId ? 'Add-on' : 'Base'}</TableCell>
            <TableCell>
              {price.adjustableQuantity
                ? `${price.minQuantity}–${price.maxQuantity ?? '∞'}`
                : 'Fixed'}
            </TableCell>
            <TableCell>
              {price.isTierDefault ? <Badge variant="default">Default</Badge> : '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
